/**
 * The access token lives in a module-scoped variable for the tab's lifetime.
 *
 * It is NEVER written to `localStorage` or `sessionStorage`: a persisted token
 * on a shared device outlives the session it was read under. The refresh token
 * is an httpOnly, `Secure`, host-only cookie this bundle cannot read at all —
 * which is also why a hard refresh arriving with no access token is normal,
 * not a signed-out state (`bootstrapSession` in `refresh.ts` settles that).
 *
 * Theme and navigation state are the only values this repo persists, and
 * neither is a credential.
 */

export interface SessionProfile {
  id: string;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
  /** Present only where the account carries a role — absent, never empty. */
  biography?: string;
}

/**
 * `backend-auth` spells this object snake_case on every response that carries
 * it — `POST /sessions`, `GET /me`, `PATCH /me`. The app holds it camelCase,
 * which is the convention `SessionProfile` already declared, so ONE mapper
 * stands between the two rather than a field rename at each call site.
 *
 * The mapper is not decoration. Handing the wire object straight to
 * `openSession` type-checks — every field is optional to a structural read —
 * and puts `undefined` into `displayName`, which the account menu then renders
 * verbatim into «حساب undefined» inside an `aria-label`. A seam that fails
 * silently on the reader's own name is exactly the seam that needs a function.
 */
export interface WireSessionProfile {
  id: string;
  display_name: string;
  handle: string;
  avatar_url: string | null;
  biography?: string | null;
}

export function toSessionProfile(wire: WireSessionProfile | null | undefined): SessionProfile | null {
  if (!wire || typeof wire.id !== 'string') return null;
  return {
    id: wire.id,
    displayName: wire.display_name,
    handle: wire.handle,
    avatarUrl: wire.avatar_url ?? null,
    ...(wire.biography ? { biography: wire.biography } : {}),
  };
}

let accessToken: string | null = null;
let profile: SessionProfile | null = null;

type SessionListener = () => void;
const listeners = new Set<SessionListener>();

function announce(): void {
  for (const listener of listeners) listener();
}

export function subscribeToSession(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readAccessToken(): string | null {
  return accessToken;
}

export function readProfile(): SessionProfile | null {
  return profile;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

export function openSession(token: string, sessionProfile: SessionProfile | null = null): void {
  accessToken = token;
  profile = sessionProfile;
  announce();
}

export function clearSession(): void {
  accessToken = null;
  profile = null;
  announce();
}
